/**
 * "Guest trials from this campaign" — the campaign page's card for the guest
 * trials its ad clicks can start: credits per visitor, trials a day, today's
 * count, and the click-verification suspension (with a lift).
 *
 * Trial credits are Fias's acquisition cost, so only a workspace owner or admin
 * may change them (the server decides: `trials.canEdit`, and the route refuses
 * anyone else). Saves go through `PUT …/trial-settings`, which changes ONLY the
 * fields sent — never the strategy PUT, which would reset every engine tunable
 * it is not sent. Each save carries the campaign version the person is looking
 * at, so a suspension the sweep wrote after the page loaded refuses the save
 * (and the card refreshes) instead of being lifted unseen. Pessimistic: what is
 * shown is what the server saved.
 */

import { useState } from 'react';
import {
  ApiError,
  type AdoptCampaignDto,
  type AdoptCampaignTrialsDto,
  type AdoptPutTrialSettingsRequest,
  type AdoptTrialSuspensionDto,
} from '@/lib/api';
import { usePutAdoptTrialSettings } from '../hooks/useAdvertisingOptimization';
import { APPROX_CREDITS_PER_BUILDER_MESSAGE } from '../constants';

/** 1 credit = $0.01. */
function creditsUsd(credits: number): string {
  return `$${(credits / 100).toFixed(2)}`;
}

function credits(n: number): string {
  return `${n.toLocaleString()} credit${n === 1 ? '' : 's'}`;
}

/** "≈ N builder messages" — approximate by design (see APPROX_CREDITS_PER_BUILDER_MESSAGE). */
export function builderMessagesHint(amount: number): string {
  const n = Math.floor(amount / APPROX_CREDITS_PER_BUILDER_MESSAGE);
  if (n === 0) {
    return `Not enough to start a builder message (one holds about ${APPROX_CREDITS_PER_BUILDER_MESSAGE} credits).`;
  }
  return `≈ ${n} builder message${n === 1 ? '' : 's'} (approximate: a message holds about ${APPROX_CREDITS_PER_BUILDER_MESSAGE} credits to start; bigger apps use more).`;
}

/** A whole number in [0, max], or null. */
function parseWhole(text: string, max: number): number | null {
  const t = text.trim();
  if (!/^\d+$/.test(t)) return null;
  const n = Number(t);
  return n <= max ? n : null;
}

function when(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

function suspensionText(s: AdoptTrialSuspensionDto | null): string {
  if (!s) return 'No reason was recorded for this suspension.';
  if (s.reason === 'manual') return `Suspended by a strategy save on ${when(s.at)}.`;
  return (
    `Suspended on ${when(s.at)}: Google did not recognise ${s.notFound} of the ${s.checked} ` +
    `trial clicks claimed on ${s.day} (UTC), so they may have been forged.`
  );
}

function errorText(error: unknown): string {
  if (error instanceof ApiError) {
    switch (error.code) {
      case 'OWNER_OR_ADMIN_REQUIRED':
        return "Only a workspace owner or admin can change a campaign's guest trials.";
      case 'VERSION_CONFLICT':
        return 'This campaign changed since you opened it (its trials may just have been suspended). The card is refreshed — check it and try again.';
      default:
        return error.message;
    }
  }
  return error instanceof Error ? error.message : 'Saving the trial settings failed';
}

export function GuestTrialsCard({
  holderId,
  campaign,
  trials,
}: {
  holderId: string;
  campaign: AdoptCampaignDto;
  trials: AdoptCampaignTrialsDto;
}) {
  const put = usePutAdoptTrialSettings(holderId, campaign.id);
  const [editing, setEditing] = useState(false);
  const [creditsText, setCreditsText] = useState('');
  const [perDayText, setPerDayText] = useState('');
  const [confirmingLift, setConfirmingLift] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  // '' = the platform default (no override).
  const draftCredits =
    creditsText.trim() === '' ? null : parseWhole(creditsText, trials.maxCampaignCredits);
  const creditsValid = creditsText.trim() === '' || draftCredits !== null;
  const draftPerDay = parseWhole(perDayText, trials.maxTrialsPerDayLimit);
  const perDayValid = draftPerDay !== null;

  const change: Omit<AdoptPutTrialSettingsRequest, 'version'> = {};
  if (creditsValid && draftCredits !== trials.creditOverride) {
    change.trialCreditAmount = draftCredits;
  }
  if (perDayValid && draftPerDay !== trials.maxTrialsPerDay) {
    change.maxTrialsPerDay = draftPerDay;
  }
  const canSave = creditsValid && perDayValid && Object.keys(change).length > 0 && !put.isPending;
  const draftAmount = draftCredits ?? trials.defaultCredits;

  const startEdit = (): void => {
    put.reset();
    setNotice(null);
    setCreditsText(trials.creditOverride === null ? '' : String(trials.creditOverride));
    setPerDayText(String(trials.maxTrialsPerDay));
    setConfirmingLift(false);
    setEditing(true);
  };

  const save = (): void => {
    if (!canSave) return;
    put.mutate(
      { version: campaign.version, ...change },
      {
        onSuccess: () => {
          setEditing(false);
          setNotice('Trial settings saved.');
        },
      },
    );
  };

  const lift = (): void => {
    put.mutate(
      { version: campaign.version, liftSuspension: true },
      {
        onSuccess: () =>
          setNotice('Suspension lifted — trials can start from this campaign again.'),
        onSettled: () => setConfirmingLift(false),
      },
    );
  };

  const worstCase = trials.maxTrialsPerDay * trials.effectiveCredits;

  return (
    <section
      aria-labelledby="adopt-guest-trials-title"
      className="rounded-lg border border-border p-4 flex flex-col gap-3"
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <h2 id="adopt-guest-trials-title" className="font-medium">
            Guest trials from this campaign
          </h2>
          <p className="text-xs text-muted-foreground">
            Each ad click from this campaign can start one guest trial — credits Fias pays for, so a
            visitor can try Fias before creating an account.
          </p>
        </div>
        {trials.canEdit && !editing && (
          <button
            type="button"
            onClick={startEdit}
            className="text-sm text-primary hover:underline"
          >
            Edit
          </button>
        )}
      </div>

      <div data-testid="guest-trials-status" className="text-sm">
        {trials.suspended ? (
          <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-rose-800">
            <p>
              <span className="font-medium">Suspended.</span> No trials start from this campaign
              until the suspension is lifted.
            </p>
            <p className="mt-1">{suspensionText(trials.suspension)}</p>
          </div>
        ) : (
          <p>
            <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-xs font-medium text-emerald-800">
              Active
            </span>{' '}
            <span className="text-muted-foreground">
              Ad clicks from this campaign can start trials.
            </span>
          </p>
        )}
      </div>

      <dl className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-1 text-sm">
        <dt className="text-muted-foreground">Credits per visitor</dt>
        <dd data-testid="guest-trials-credits">
          {credits(trials.effectiveCredits)} ({creditsUsd(trials.effectiveCredits)})
          {trials.creditOverride === null ? ' — the platform default' : ''}
          <span className="block text-xs text-muted-foreground">
            {builderMessagesHint(trials.effectiveCredits)}
          </span>
        </dd>
        <dt className="text-muted-foreground">Trials a day (max)</dt>
        <dd data-testid="guest-trials-per-day">{trials.maxTrialsPerDay}</dd>
        <dt className="text-muted-foreground">Started today (UTC)</dt>
        <dd data-testid="guest-trials-today">
          {trials.claimedToday} of {trials.maxTrialsPerDay}
        </dd>
        <dt className="text-muted-foreground">Most Fias pays a day</dt>
        <dd data-testid="guest-trials-worst-case">
          {credits(worstCase)} ({creditsUsd(worstCase)})
        </dd>
      </dl>

      {trials.suspended && trials.canEdit && !editing && (
        <div className="flex flex-col gap-2 border-t border-border pt-3">
          {confirmingLift ? (
            <div
              role="group"
              aria-label="Confirm lifting the suspension"
              className="flex flex-col gap-2"
            >
              <p className="text-sm">
                Lift the suspension? Trials from this campaign start again right away. If the
                unrecognised clicks were forged, lifting lets that source mint trials again — up to{' '}
                {trials.maxTrialsPerDay} a day.
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={lift}
                  disabled={put.isPending}
                  className="px-3 py-1.5 rounded-md bg-rose-600 text-white text-sm disabled:opacity-50"
                >
                  {put.isPending ? 'Lifting…' : 'Yes, lift the suspension'}
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmingLift(false)}
                  className="px-3 py-1.5 rounded-md border border-border text-sm"
                >
                  Keep it suspended
                </button>
              </div>
            </div>
          ) : (
            <div>
              <button
                type="button"
                onClick={() => {
                  put.reset();
                  setNotice(null);
                  setConfirmingLift(true);
                }}
                className="px-3 py-1.5 rounded-md border border-rose-300 text-rose-700 text-sm hover:bg-rose-50"
              >
                Lift suspension
              </button>
            </div>
          )}
        </div>
      )}

      {editing && (
        <div className="flex flex-col gap-3 border-t border-border pt-3">
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-muted-foreground">
              Credits per visitor (0–{trials.maxCampaignCredits}; empty = the platform default,{' '}
              {trials.defaultCredits})
            </span>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="off"
              className="w-40 rounded-md border border-border bg-background px-2 py-1.5"
              value={creditsText}
              onChange={(e) => setCreditsText(e.target.value)}
              placeholder={`${trials.defaultCredits} (default)`}
            />
          </label>
          {creditsValid ? (
            <p className="text-xs text-muted-foreground" data-testid="guest-trials-draft-hint">
              {credits(draftAmount)} ({creditsUsd(draftAmount)}) per visitor.{' '}
              {builderMessagesHint(draftAmount)}
            </p>
          ) : (
            <p className="text-xs text-rose-600">
              Enter a whole number of credits from 0 to {trials.maxCampaignCredits}, or leave it
              empty for the platform default.
            </p>
          )}
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-muted-foreground">
              Trials a day (0–{trials.maxTrialsPerDayLimit})
            </span>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="off"
              className="w-40 rounded-md border border-border bg-background px-2 py-1.5"
              value={perDayText}
              onChange={(e) => setPerDayText(e.target.value)}
            />
          </label>
          {!perDayValid && (
            <p className="text-xs text-rose-600">
              Enter a whole number from 0 to {trials.maxTrialsPerDayLimit}.
            </p>
          )}
          {creditsValid && perDayValid && (
            <p className="text-xs text-muted-foreground">
              At most {credits(draftAmount * (draftPerDay ?? 0))} (
              {creditsUsd(draftAmount * (draftPerDay ?? 0))}) of trials a day from this campaign.
            </p>
          )}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={save}
              disabled={!canSave}
              className="px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-sm disabled:opacity-50"
            >
              {put.isPending ? 'Saving…' : 'Save trial settings'}
            </button>
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="px-3 py-1.5 rounded-md border border-border text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {!trials.canEdit && (
        <p className="text-xs text-muted-foreground">
          Only a workspace owner or admin can change these.
        </p>
      )}

      {put.isError && (
        <p className="text-sm text-rose-600" role="alert">
          {errorText(put.error)}
        </p>
      )}
      {notice && !put.isError && (
        <p className="text-sm text-emerald-700" role="status">
          {notice}
        </p>
      )}
    </section>
  );
}
